/* assets/littleg-upload-confirmation-revision.js — FAMILY-CONFIRMATION-REVISION-07
 * Owner: Upload U04 Today Checklist presentation projection plus content/confirmation revision contract.
 * Existing Upload Workspace remains the U01–U06 state/model owner and transport caller.
 * Visual owner: assets/littleg-today-checklist.css owns the unique U04 Desktop/Mobile/PWA/iOS layer.
 * Confirm validation is contributed through LittleGUploadTransport.registerConfirmContributor().
 * Projection observes only source state and checklist mutations; generated copy cannot retrigger itself.
 * Boundary: no checklist truth, recognition_state, guardian_state, storage key, backend or parent-auth ownership.
 */
(function littleGUploadConfirmationRevision(window, document) {
  'use strict';

  var VERSION = '2026-08-08-confirmation-revision-07';
  var ROOT_SELECTOR = '[data-littleg-upload-workspace]';
  var observers = typeof WeakMap === 'function' ? new WeakMap() : null;

  function number(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }
  function clean(value) { return String(value == null ? '' : value).trim(); }
  function queryAll(root, selector) { return Array.prototype.slice.call(root.querySelectorAll(selector)); }
  function familyData() { return window.LittleGFamilyData || null; }
  function setText(node, value) {
    if (!node) return;
    var next = String(value == null ? '' : value);
    if (node.textContent !== next) node.textContent = next;
  }
  function revisions(root) {
    return {
      content_revision: number(root.getAttribute('data-content-revision'), 1),
      confirmed_revision: number(root.getAttribute('data-confirmed-revision'), 0)
    };
  }
  function write(root, content, confirmed, reason) {
    root.setAttribute('data-content-revision', String(content));
    root.setAttribute('data-confirmed-revision', String(confirmed));
    root.setAttribute('data-confirm-revision-state', content === confirmed && confirmed > 0 ? 'confirmed' : 'confirmation_required');
    if (reason) root.setAttribute('data-confirm-invalidation-reason', reason);
    var owner = familyData();
    if (owner && typeof owner.getNoticeDraft === 'function' && typeof owner.saveNoticeDraft === 'function') {
      var draft = owner.getNoticeDraft();
      if (draft) owner.saveNoticeDraft(Object.assign({}, draft, {
        content_revision: content,
        confirmed_revision: confirmed
      }));
    }
  }
  function invalidate(root, reason) {
    var value = revisions(root);
    write(root, value.content_revision + 1, 0, reason || 'content_changed');
    document.dispatchEvent(new CustomEvent('littleg:notice-confirmation-invalidated', {
      bubbles: true,
      detail: { content_revision: value.content_revision + 1, reason: reason || 'content_changed' }
    }));
  }
  function confirmCurrent(root) {
    var value = revisions(root);
    write(root, value.content_revision, value.content_revision, 'guardian_confirmed');
    document.dispatchEvent(new CustomEvent('littleg:notice-confirmation-revision', {
      bubbles: true,
      detail: { content_revision: value.content_revision, confirmed_revision: value.content_revision }
    }));
  }
  function isContentControl(target) {
    return !!(target && target.closest && target.closest(
      '[data-field-value],[data-prep-item-toggle],[data-manual-activity-name],[data-manual-activity-date],[data-manual-item-input],[data-packing-custom-input],[data-upload-child-picker],[data-notice-deadline]'
    ));
  }
  function isContentAction(target) {
    var action = target && target.closest ? target.closest('[data-upload-action],[data-manual-remove-index]') : null;
    if (!action) return '';
    var name = action.getAttribute('data-upload-action') || 'remove-manual-item';
    return ['add-item', 'remove-field', 'add-custom-packing-item', 'remove-packing-item', 'remove-manual-item'].indexOf(name) !== -1 ? name : '';
  }
  function registerContributor(root) {
    var transport = window.LittleGUploadTransport;
    if (!transport || typeof transport.registerConfirmContributor !== 'function') return;
    transport.registerConfirmContributor('confirmation-revision', function (payload) {
      var next = Object.assign({}, payload || {});
      var value = revisions(root);
      var confirmation = {
        content_revision: value.content_revision,
        confirmed_revision: value.confirmed_revision,
        confirmed_at: value.content_revision === value.confirmed_revision ? new Date().toISOString() : ''
      };
      if (!value.confirmed_revision || value.confirmed_revision !== value.content_revision) {
        root.setAttribute('data-confirm-revision-error', 'confirmation_revision_mismatch');
        var error = new Error('confirmation_revision_mismatch');
        error.code = 'confirmation_revision_mismatch';
        error.user_message = '內容已修改，請重新核對後再提交。';
        error.retryable = false;
        throw error;
      }
      root.removeAttribute('data-confirm-revision-error');
      next.confirmation = confirmation;
      next.notice = Object.assign({}, next.notice || {}, { confirmation: confirmation });
      return next;
    }, 50);
  }
  function fieldValue(root, key) {
    var row = root.querySelector('[data-notice-field][data-field-key="' + key + '"]');
    var input = row && row.querySelector('[data-field-value]');
    if (!row || row.getAttribute('data-guardian-state') === 'removed') return '';
    return clean(input && input.value);
  }
  function childContext(root) {
    var binding = window.LittleGUploadChildBinding;
    if (binding && typeof binding.getSnapshot === 'function') {
      var snapshot = binding.getSnapshot();
      var nickname = clean(snapshot && snapshot.nickname_snapshot);
      if (nickname) return nickname;
    }
    var rootLabel = clean(root.getAttribute('data-upload-child-label'));
    if (rootLabel) return rootLabel;
    var picker = root.querySelector('[data-upload-child-picker]');
    if (picker && clean(picker.value)) {
      var option = picker.options && picker.options[picker.selectedIndex];
      var label = clean(option && option.textContent);
      if (label && !/正在讀取|未指定/.test(label)) return label;
    }
    return fieldValue(root, 'child_name') || '未指定孩子';
  }
  function itemIcon(label) {
    var value = clean(label).toLowerCase();
    if (/書包|背包|backpack/.test(value)) return '🎒';
    if (/鉛筆|筆盒|文具|pencil/.test(value)) return '✏️';
    if (/水樽|水壺|水瓶|water/.test(value)) return '🧃';
    if (/聯絡|手冊|功課簿|簿|book/.test(value)) return '📒';
    if (/雨傘|傘|umbrella/.test(value)) return '☂️';
    if (/紙巾|濕紙巾|tissue/.test(value)) return '🧻';
    if (/口罩|mask/.test(value)) return '😷';
    if (/防曬|太陽油|sun/.test(value)) return '🧴';
    if (/帽|cap/.test(value)) return '🧢';
    if (/衣|外套|衫|clothes/.test(value)) return '👕';
    if (/藥|medicine/.test(value)) return '🩹';
    if (/餐|食物|小食|snack/.test(value)) return '🍱';
    return '✨';
  }
  function groupCopy(group) {
    if (group === 'required') return '必帶物品';
    if (group === 'suggested') return 'LittleG 建議帶';
    return '家長加的物品';
  }
  function ensureProjectionNode(host, heading, selector, className) {
    var node = host.querySelector(selector);
    if (node) return node;
    node = document.createElement('p');
    node.className = className;
    node.setAttribute(selector.slice(1, -1), '');
    if (heading && heading.parentNode === host) heading.insertAdjacentElement('afterend', node);
    else host.insertBefore(node, host.firstChild || null);
    return node;
  }
  function contextLine(root, child) {
    var date = fieldValue(root, 'activity_date');
    var time = fieldValue(root, 'activity_time');
    var activity = fieldValue(root, 'activity_name');
    var location = fieldValue(root, 'location');
    var parts = [child];
    var timing = [date, time].filter(Boolean).join(' ');
    if (timing) parts.push(timing);
    if (activity) parts.push(activity);
    if (location) parts.push(location);
    return parts.join(' · ');
  }
  function projectTodayChecklist(root) {
    var view = root.querySelector('[data-upload-view="prep"]');
    var host = view && view.querySelector('[data-prep-checklist-host]');
    var list = host && host.querySelector('[data-prep-checklist]');
    var heading = host && host.querySelector('[data-upload-prep-heading]');
    var progress = host && host.querySelector('[data-prep-progress-status]');
    var cta = host && host.querySelector('[data-upload-action="prep-continue"]');
    if (!host || !list || !heading || !progress || !cta) return;
    host.setAttribute('data-daily-checklist', 'true');
    setText(heading, '今日清單');
    heading.setAttribute('data-daily-focus-label', '專注模式');
    var child = childContext(root);
    var subtitle = ensureProjectionNode(host, heading, '[data-daily-checklist-subtitle]', 'lgu-daily-checklist__subtitle');
    var context = ensureProjectionNode(host, subtitle, '[data-daily-checklist-context]', 'lgu-daily-checklist__context');
    setText(subtitle, child === '未指定孩子'
      ? '請先返回通告內容指定孩子，再檢查今天需要帶的物品。'
      : '一起幫 ' + child + ' 檢查並準備好今天需要帶的物品。');
    setText(context, contextLine(root, child));
    var rows = queryAll(list, '[data-prep-item]');
    var counts = { required: 0, suggested: 0, custom: 0 };
    var checkedCounts = { required: 0, suggested: 0, custom: 0 };
    var firstRows = {};
    var checked = 0;
    rows.forEach(function (row) {
      var group = clean(row.getAttribute('data-prep-group')) || 'custom';
      if (!Object.prototype.hasOwnProperty.call(counts, group)) group = 'custom';
      var checkbox = row.querySelector('[data-prep-item-toggle]');
      var label = clean(row.querySelector('[data-prep-item-label]') && row.querySelector('[data-prep-item-label]').textContent);
      var copy = row.querySelector('label > span');
      var isChecked = !!(checkbox && checkbox.checked);
      counts[group] += 1;
      checkedCounts[group] += isChecked ? 1 : 0;
      checked += isChecked ? 1 : 0;
      if (!firstRows[group]) firstRows[group] = row;
      row.setAttribute('data-daily-checklist-item', 'true');
      row.setAttribute('data-checked', isChecked ? 'true' : 'false');
      row.removeAttribute('data-group-first');
      row.removeAttribute('data-group-heading');
      row.removeAttribute('data-group-count');
      row.removeAttribute('data-group-status');
      if (copy) copy.setAttribute('data-item-icon', itemIcon(label));
    });
    ['required', 'suggested', 'custom'].forEach(function (group) {
      var first = firstRows[group];
      if (!first) return;
      first.setAttribute('data-group-first', 'true');
      first.setAttribute('data-group-heading', groupCopy(group));
      first.setAttribute('data-group-count', String(counts[group]));
      first.setAttribute('data-group-status', counts[group] > 0 && checkedCounts[group] === counts[group] ? '全部完成 ✓' : '');
    });
    var total = rows.length;
    var remaining = Math.max(0, total - checked);
    var percent = total ? Math.round((checked / total) * 100) : 0;
    var complete = total > 0 && remaining === 0;
    host.setAttribute('data-daily-child', child);
    host.setAttribute('data-daily-progress', String(percent));
    host.setAttribute('data-daily-checklist-complete', complete ? 'true' : 'false');
    progress.setAttribute('data-progress-count', checked + ' / ' + total);
    progress.setAttribute('data-progress-remaining', String(remaining));
    progress.style.setProperty('--daily-progress', percent + '%');
    setText(progress, total === 0
      ? '清單未有物品，請返回上一步加入需要攜帶的項目。'
      : (complete ? '全部帶齊，可以進行家長確認。' : '還有 ' + remaining + ' 項待完成'));
    var labelNode = cta.querySelector('span');
    cta.disabled = total === 0;
    cta.setAttribute('aria-disabled', total === 0 ? 'true' : 'false');
    cta.setAttribute('data-daily-incomplete-count', String(remaining));
    setText(labelNode, total ? '我已檢查完成' : '清單未有物品');
  }
  function scheduleProjection(root) {
    if (!root || root.getAttribute('data-daily-checklist-projection-pending') === 'true') return;
    root.setAttribute('data-daily-checklist-projection-pending', 'true');
    window.requestAnimationFrame(function () {
      root.removeAttribute('data-daily-checklist-projection-pending');
      projectTodayChecklist(root);
    });
  }
  function observeProjection(root) {
    if (typeof MutationObserver !== 'function') return;
    if (observers && observers.has(root)) return;
    var list = root.querySelector('[data-prep-checklist]');
    var observer = new MutationObserver(function () { scheduleProjection(root); });
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-upload-state', 'data-upload-child-binding-state', 'data-guardian-state']
    });
    if (list) observer.observe(list, { subtree: true, childList: true });
    if (observers) observers.set(root, observer);
  }
  function bind(root) {
    if (!root || root.getAttribute('data-confirm-revision-bound') === VERSION) return;
    root.setAttribute('data-confirm-revision-bound', VERSION);
    root.setAttribute('data-daily-necessity-contract', 'today-checklist-parent-relief-v1');
    var owner = familyData();
    var draft = owner && typeof owner.getNoticeDraft === 'function' ? owner.getNoticeDraft() : null;
    write(root, number(draft && draft.content_revision, 1), number(draft && draft.confirmed_revision, 0), 'initial');
    registerContributor(root);
    observeProjection(root);
    scheduleProjection(root);
    root.addEventListener('input', function (event) {
      if (isContentControl(event.target)) invalidate(root, 'input_changed');
      scheduleProjection(root);
    });
    root.addEventListener('change', function (event) {
      if (isContentControl(event.target) && event.target.type === 'checkbox') invalidate(root, 'selection_changed');
      scheduleProjection(root);
    });
    root.addEventListener('click', function (event) {
      var confirm = event.target.closest('[data-upload-action="confirm-notice"]');
      if (confirm && root.contains(confirm)) {
        confirmCurrent(root);
        return;
      }
      var action = isContentAction(event.target);
      if (action) window.setTimeout(function () {
        invalidate(root, action);
        scheduleProjection(root);
      }, 0);
      if (event.target.closest('[data-upload-action="packing-continue"],[data-upload-action="prep-back"],[data-upload-action="confirm-back"]')) {
        window.setTimeout(function () { scheduleProjection(root); }, 0);
      }
    }, true);
    root.addEventListener('littleg:upload-child-change', function () {
      invalidate(root, 'child_changed');
      scheduleProjection(root);
    });
    root.addEventListener('littleg:upload-state-change', function (event) {
      var state = clean(event && event.detail && event.detail.state);
      if (state === 'error' && root.getAttribute('data-confirm-revision-error') === 'confirmation_revision_mismatch') {
        root.setAttribute('data-upload-error-code', 'confirmation_revision_mismatch');
        root.removeAttribute('data-confirm-revision-error');
      }
      scheduleProjection(root);
    });
  }
  function boot(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll(ROOT_SELECTOR), bind);
  }

  window.LittleGUploadConfirmationRevision = Object.freeze({
    version: VERSION,
    getSnapshot: function (root) { return revisions(root || document.querySelector(ROOT_SELECTOR)); }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  else boot(document);
  document.addEventListener('littleg:upload-child-change', function () {
    Array.prototype.forEach.call(document.querySelectorAll(ROOT_SELECTOR), scheduleProjection);
  });
  document.addEventListener('shopify:section:load', function (event) { boot(event.target || document); });
})(window, document);
